import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { IFieldConfig } from 'src/app/domain/IFieldConfig';
import { SystemTypeIdEnum } from 'src/app/domain/registration/enums/system-type.enum';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { IPartner } from 'src/app/domain/registration/models/IPartner';
import { IPlan } from 'src/app/domain/registration/models/IPlan';
import { ISystem } from 'src/app/domain/registration/models/ISystem';
import { AbstractCrudCES } from 'src/app/shared/components/crud/abstract-crud-ces';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { PermissionService } from 'src/app/shared/permission.service';
import { PartnerService } from '../../partners/crud-service';
import { SystemService } from '../../systems/crud-service';
import { CES_CONFIG_FIELDS } from '../crud-config';
import { PlanEditReason } from './plan-edit-reason-dialog/plan-edit-reason-dialog.component';

@Component({
  selector: 'app-plan-ces',
  templateUrl: './crud-ces.component.html',
  styleUrls: ['./crud-ces.component.sass'],
  providers: [SystemService, PartnerService],
})
export class PlanCESComponent extends AbstractCrudCES<IPlan> implements OnInit, OnDestroy {
  override config: IFieldConfig[] = CES_CONFIG_FIELDS;
  override title = 'PLANO';

  private destroy$ = new Subject<void>();
  partnersSelected: IPartner[] = [];

  private setupFormSubscriptions(): void {
    this.form.get('type')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: any) => {
        this.setValue('system', '');
        this.setValue('dueDate', '');
        // Simplificacao: modal de sistema lista TODAS as solucoes (sem filtro por tipo).
        this.getFieldConfig('system').componentData = {
          params: {},
        };
      });

    this.form.get('system')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((systemName: any) => {
        if (systemName) {
          this.loadSystemDueDate(systemName);
        } else {
          this.setValue('dueDate', '');
        }
      });
  }

  private loadSystemDueDate(systemName: string): void {
    this.systemService.listAll({ limit: 1000, page: 0, filter: '' })
      .pipe(takeUntil(this.destroy$))
      .subscribe(([data]: any) => {
        const selectedSystem = data.find((item: ISystem) => item.name === systemName);
        if (selectedSystem?.dueDate) {
          this.setValue('dueDate', selectedSystem.dueDate);
          this.setDisabled('dueDate', true);
        }
      });
  }

  constructor(
    public override fb: FormBuilder,
    private dialogRef: MatDialogRef<PlanCESComponent>,
    @Inject(MAT_DIALOG_DATA) public override data: { type: TypeCesEnum; element: IPlan },
    private cdr: ChangeDetectorRef,
    private systemService: SystemService,
    private partnerService: PartnerService,
    private dialog: MatDialog,
    public permissionService: PermissionService,
  ) {
    super(fb);
    this.typeCes = data.type;
    this.title = this.getTitleByType(data.type);
  }

  private getTitleByType(type: TypeCesEnum): string {
    switch (type) {
      case TypeCesEnum.NEW: return 'NOVO PLANO';
      case TypeCesEnum.EDIT: return 'EDITAR PLANO';
      case TypeCesEnum.SHOW: return 'VISUALIZAR PLANO';
      default: return 'PLANO';
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.setupFormSubscriptions();
    this.patchFormData();
  }

  private patchFormData(): void {
    if (this.data?.element) {

      const element = this.data.element as any;
      let systemName = element.system?.name || element.system;
      let systemId = element.systemId || element.system?.id;
      this.partnersSelected = element.partnersList;

      const formData: any = {
        ...element,
        system: systemName,
        systemId: systemId,
      };

      setTimeout(() => {
        this.form.patchValue(formData);
        console.log('patchFormData - form patched, current values:', this.form.value);
      }, 0);

      if (this.typeCes === TypeCesEnum.EDIT && systemName) {
        this.loadSystemDueDate(systemName);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  onSubmit(value: any): void {
    const processedValue = this.processFormValue(value);

    if (this.typeCes === TypeCesEnum.EDIT) {
      this.showEditReasonDialog(processedValue);
    } else {
      this.apply(processedValue);
    }
  }

  private processFormValue(value: any): any {
    let systemId = value.systemId;
    let systemName = value.system;

    if (value.system && typeof value.system === 'object' && value.system.id) {
      systemId = value.system.id;
      systemName = value.system.name;
      console.log('Sistema processado como objeto:', { id: systemId, name: systemName });
    }

    const processedValue: any = {
      systemId: systemId,
      system: systemName,
      type: value.type,
      terminalQuantity: value.terminalQuantity,
      monthlyValue: value.monthlyValue,
      adhesionFee: value.adhesionFee,
      referenceValue: value.referenceValue || null,
      percentage: value.percentage || null,
      isDaysCount: value.isDaysCount,
      partners: this.partnersSelected?.map((partner: IPartner) => partner.id) ?? []
    };

    if (this.typeCes === TypeCesEnum.EDIT && this.data?.element?.id) {
      processedValue.id = this.data.element.id;
    }

    return processedValue;
  }

  private showEditReasonDialog(value: any): void {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      component: PlanEditReason,
      data: { title: 'Motivo da alteração', typeCes: this.typeCes },
      callBack: (data: any) => {
        if (data?.result) {
          value.updateReason = data.updateReason;
          this.apply(value);
        }
      },
    });
  }

  private apply(value: any): void {
    this.systemService
      .listAll({ limit: 1000, page: 0, filter: '' })
      .pipe(takeUntil(this.destroy$))
      .subscribe(([data]: any) => {
        const processedValue = this.processSystemData(value, data);
        this.dialogRef.close({ element: processedValue });
      });
  }

  private processSystemData(value: any, systemsData: ISystem[]): any {
    const systemName = typeof value.system === 'string' ? value.system : value.system?.name;

    if (!systemName || systemName.trim() === '') {

      if (value.systemId) {
        const selectedSystem = systemsData.find(
          (item: ISystem) => item.id === value.systemId
        );

        if (selectedSystem) {
          console.log('Sistema encontrado por ID:', selectedSystem.name);
          return {
            ...value,
            systemId: selectedSystem.id,
            system: selectedSystem.name,
            // Plano herda o tipo do sistema escolhido (fallback: campo "Tipo").
            type: selectedSystem.type ?? this.convertTypeToId(value.type)
          };
        }
      }

      console.error('Sistema não encontrado - name vazio e systemId não encontrado');
      console.error('Sistemas disponíveis:', systemsData.map(s => s.name));
      throw new Error('Sistema não encontrado: nome vazio e ID não encontrado');
    }

    const selectedSystem = systemsData.find(
      (item: ISystem) => item.name === systemName
    );

    if (!selectedSystem) {
      console.error('Sistema não encontrado:', systemName);
      console.error('Sistemas disponíveis:', systemsData.map(s => s.name));
      throw new Error(`Sistema não encontrado: ${systemName}`);
    }

    return {
      ...value,
      systemId: selectedSystem.id,
      system: selectedSystem.name,
      // Plano herda o tipo do sistema escolhido (fallback: campo "Tipo").
      type: selectedSystem.type ?? this.convertTypeToId(value.type)
    };
  }

  private convertTypeToId(type: any): number {
    if (type && typeof type === 'string') {
      return SystemTypeIdEnum[type as keyof typeof SystemTypeIdEnum];
    }
    return type;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  changeType(type: TypeCesEnum): void {
    this.typeCes = type;
    this.dialogRef.close({ changeType: type });
  }

  delete(): void {
    this.dialogRef.close({ delete: true });
  }

  selectPartners(event: any): void {
    this.partnersSelected = event?.length > 0
      ? event.map((partner: any) => ({
        id: partner.id,
        name: partner.name,
      }))
      : [];
  }
}